import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { bumpOnepageGuideStorageVersion } from "./onepageGuideSignals";
import type { OnepageGuideFlag } from "./onepageGuideTypes";

/**
 * 一屏引导本地「预约 + 兑现」（D15 / fe-spec §2.4）。
 *
 * 值：`Record<ownerId, "pending" | "seen">`，按 ownerId 隔离（同设备切账号互不影响）。
 * - 预约 {@link armOnepageGuide}：仅当该 owner **无值**时写 `pending`（AC-12：已有值不覆盖）
 * - 兑现 {@link markOnepageGuideSeen}：弹出瞬间写 `seen`，并在内存标记本会话已展示（R9）
 * - 读写全部 try/catch；**读失败按「无值」处理**（不弹）
 *
 * 只有 onboarding 首墙会预约 ⇒ 老账号永远没有 `pending`（AC-18）。
 */

type OnepageGuideStateMap = Record<string, OnepageGuideFlag>;

/** 写 `seen` 失败（存储满 / 隐私模式）时的同会话兜底（R9） */
const shownThisSession = new Set<string>();

function normalizeOwnerId(ownerId: string | null | undefined): string {
  return typeof ownerId === "string" ? ownerId.trim() : "";
}

function isFlag(value: unknown): value is OnepageGuideFlag {
  return value === "pending" || value === "seen";
}

/** `null` = 读失败（调用方按无值处理，且不得据此覆盖写） */
function readStateMap(): OnepageGuideStateMap | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(
      LOCAL_STORAGE_KEYS.ONEPAGE_GUIDE_STATE,
    );
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: OnepageGuideStateMap = {};
    for (const [key, value] of Object.entries(
      parsed as Record<string, unknown>,
    )) {
      if (isFlag(value)) {
        out[key] = value;
      }
    }
    return out;
  } catch {
    return null;
  }
}

function writeStateMap(map: OnepageGuideStateMap): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.ONEPAGE_GUIDE_STATE,
      JSON.stringify(map),
    );
    return true;
  } catch {
    return false;
  }
}

/** 当前 owner 的引导状态；无值 / 读失败 / 无 owner → `null` */
export function readOnepageGuideFlag(
  ownerId: string | null | undefined,
): OnepageGuideFlag | null {
  const id = normalizeOwnerId(ownerId);
  if (!id) {
    return null;
  }
  return readStateMap()?.[id] ?? null;
}

/**
 * 是否该引导仍待兑现：本地 `pending` 且本会话未展示过。
 * 写 `seen` 失败时本地仍是 `pending`，由内存标记兜住同会话不重复（R9）。
 */
export function isOnepageGuidePending(
  ownerId: string | null | undefined,
): boolean {
  const id = normalizeOwnerId(ownerId);
  if (!id || shownThisSession.has(id)) {
    return false;
  }
  return readOnepageGuideFlag(id) === "pending";
}

/**
 * 预约（D21：onboarding 首付费墙打开时）。仅当该 owner 无值时写 `pending`。
 * @returns 是否本次写入了 `pending`
 */
export function armOnepageGuide(ownerId: string | null | undefined): boolean {
  const id = normalizeOwnerId(ownerId);
  if (!id) {
    return false;
  }
  const map = readStateMap();
  // 读失败不写：可能覆盖掉一个读不出来的 `seen`
  if (!map || map[id]) {
    return false;
  }
  map[id] = "pending";
  const ok = writeStateMap(map);
  if (ok) {
    bumpOnepageGuideStorageVersion();
  }
  return ok;
}

/** 兑现：弹出瞬间调用。写失败不影响本次展示（内存标记已置位）。 */
export function markOnepageGuideSeen(ownerId: string | null | undefined): void {
  const id = normalizeOwnerId(ownerId);
  if (!id) {
    return;
  }
  shownThisSession.add(id);
  const map = readStateMap();
  // 读失败不写（会抹掉其他 owner 的值）；本会话由内存标记兜底
  if (map) {
    map[id] = "seen";
    writeStateMap(map);
  }
  bumpOnepageGuideStorageVersion();
}

/** 测试复位：清内存会话标记 */
export function resetOnepageGuideSessionMemory(): void {
  shownThisSession.clear();
}
